import { isUserAnAdmin, isUserSuperAdmin, isUserAPartnerProtector } from '../api/user';
import AuthService from 'services/authService';
import { getExpertById } from '../api/expertQueries/expertQueriesGraphQL';
import environment from '../environment';
import { clearData, setSession, setUser } from '@asurion-core/frontend-utils/lib/WebObservability';
import { maskEmailAddress } from './formatUtil';
import { ACCOUNT_USER } from '../constants/roles';

/**
 * Resolves admin and account-user flags for the current user (e.g. during login flows).
 * Use this instead of calling isUserAnAdmin() and roles separately when you need both.
 * @returns {{ isAdmin: boolean, isAccountUser: boolean, isAdminOrAccountUser: boolean }}
 */
export const getLoginRoleInfo = async () => {
  const isAdmin = await isUserAnAdmin();
  const isAccountUser = AuthService.current().roles?.includes(ACCOUNT_USER) ?? false;
  return {
    isAdmin,
    isAccountUser,
    isAdminOrAccountUser: isAdmin || isAccountUser,
  };
};

export const syncStoreWithCurrentUserState = async (updateUser) => {
  const currentUser = AuthService.current();
  if (!currentUser) {
    if (environment.observability.isTelemetryEnabled) {
      clearData();
    }

    return;
  }

  const getSimplrExpert = async () => {
    const simplrExpertId = currentUser.simplrExpert?.id;

    return simplrExpertId ? await getExpertById(simplrExpertId) : null;
  };

  const isAccountUser = currentUser.roles?.includes(ACCOUNT_USER);

  const [expert, isAdmin, isSuperAdmin, isAPartnerProtector] = await Promise.all([
    getSimplrExpert(),
    // TODO(pavlozaporozkyi|SPD-???|2024-08-26): Replace with one call instead of 4 if possible. Or get it during login once and store in the session-data in storage
    //  this would be easier to do after we migrate roles related functionality to auth service
    isUserAnAdmin(),
    isUserSuperAdmin(),
    isUserAPartnerProtector(),
  ]);

  const user = {
    id: expert.id,
    firstName: expert.firstName,
    isAdmin: isAdmin || isAccountUser,
    isAccountUser,
    isSuperAdmin,
    isAPartnerProtector,
    loggedIn: true,
    expertReviewPopupAcknowledged: expert.expertReviewPopupAcknowledged,
    expertName: `${expert.firstName} ${expert.lastName}`,
    expertClass: expert.expertClass,
    whitelist: expert.whitelist || [],
    expertType: expert.expertType,
    userPolicies: expert.userPtr?.userRolesPolicies || [],
  };

  if (environment.observability.isTelemetryEnabled) {
    setSession(currentUser.sessionToken);
    setUser({
      id: user.id,
      username: user.expertName,
      email: maskEmailAddress(currentUser.email),
    });
  }

  await updateUser(user);
};
